import axios from "axios";
import { useEffect, useState } from "react";
import {
  Plus,
  Loader2,
  Save,
  Trash2,
  Pencil,
  X,
  KeyRound,
  Send,
  Mail,
  ShieldCheck,
  CircleSlash,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyForm = () => ({
  id: null,
  name: "",
  email: "",
  profile_id: "",
  method: "password",
  password: "",
  is_active: true,
});

export default function AccessUsersPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetingId, setResetingId] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [search, setSearch] = useState("");

  const filteredUsers = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.profile_name || "").toLowerCase().includes(q)
    );
  });

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, pRes] = await Promise.all([
        axios.get(`${API}/access/users`),
        axios.get(`${API}/access/profiles`),
      ]);
      setUsers(uRes.data || []);
      setProfiles(pRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const profilesById = Object.fromEntries(profiles.map((p) => [p.id, p]));

  const handleNew = () => setEditing(emptyForm());

  const handleEdit = (u) => {
    setEditing({
      id: u.id,
      name: u.name,
      email: u.email,
      profile_id: u.profile_id,
      is_active: u.is_active,
      method: "password",
      password: "",
    });
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Excluir o usuário "${u.name}"?`)) return;
    try {
      await axios.delete(`${API}/access/users/${u.id}`);
      toast.success("Usuário excluído");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível excluir");
    }
  };

  const handleResetPassword = async (u) => {
    if (!resetPwd || resetPwd.length < 6) {
      toast.error("Informe uma senha de pelo menos 6 caracteres");
      return;
    }
    try {
      await axios.post(`${API}/access/users/${u.id}/reset-password`, {
        password: resetPwd,
      });
      toast.success("Senha redefinida");
      setResetingId(null);
      setResetPwd("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao redefinir senha");
    }
  };

  const handleSendInvite = async (u) => {
    try {
      const res = await axios.post(`${API}/access/users/${u.id}/send-invite`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else if (res.data.invite_password) {
        toast.warning(
          `${res.data.message} Senha temporária: ${res.data.invite_password}`,
          { duration: 15000 }
        );
      } else {
        toast.warning(res.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar convite");
    }
  };

  const handleSave = async () => {
    if (!editing.name.trim() || !editing.profile_id) {
      toast.error("Preencha nome e perfil");
      return;
    }
    if (!editing.id && !editing.email.trim()) {
      toast.error("Informe o e-mail");
      return;
    }

    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/access/users/${editing.id}`, {
          name: editing.name.trim(),
          profile_id: editing.profile_id,
          is_active: editing.is_active,
        });
        toast.success("Usuário atualizado");
      } else {
        const payload = {
          name: editing.name.trim(),
          email: editing.email.trim().toLowerCase(),
          profile_id: editing.profile_id,
          method: editing.method,
        };
        if (editing.method === "password") {
          if (!editing.password || editing.password.length < 6) {
            toast.error("Senha precisa ter ao menos 6 caracteres");
            setSaving(false);
            return;
          }
          payload.password = editing.password;
        }
        const res = await axios.post(`${API}/access/users`, payload);
        // Backend may surface a temporary password in `invite_password` when
        // SMTP delivery failed during the invite flow.
        const pw = res.data?.invite_password;
        if (pw) {
          toast.warning(`SMTP indisponível. Senha temporária: ${pw}`, { duration: 15000 });
        } else if (editing.method === "invite") {
          toast.success("Convite enviado por e-mail");
        } else {
          toast.success("Usuário criado");
        }
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando usuários…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!editing && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1">
            <p className="text-xs text-slate-500 max-w-xl">
              Cadastre os usuários internos da empresa e atribua um perfil para cada um.
              O perfil determina as permissões e o que ele consegue ver.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nome, e-mail ou perfil…"
                data-testid="users-search-input"
                className="pl-8 w-64 h-9 text-sm"
              />
            </div>
            <Button
              data-testid="new-user-button"
              onClick={handleNew}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Novo usuário
            </Button>
          </div>
        </div>
      )}

      {!editing && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Usuário
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Perfil
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isMe = u.id === me?.id;
                const profile = profilesById[u.profile_id];
                return (
                  <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 align-middle">
                      <div className="font-medium text-slate-900">
                        {u.name}
                        {isMe && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            você
                          </span>
                        )}
                        {u.role === "admin" && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-brand-blue">
                            <ShieldCheck className="w-3 h-3" /> admin
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {u.profile_name || "—"}
                      {profile && (
                        <div className="text-[11px] text-slate-500">
                          {profile.data_scope === "company" ? "Dados da empresa" : "Apenas dados próprios"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_active ? (
                        <span className="text-xs text-emerald-700 font-medium">Ativo</span>
                      ) : (
                        <span className="text-xs text-red-600 font-medium inline-flex items-center gap-1">
                          <CircleSlash className="w-3 h-3" /> Desativado
                        </span>
                      )}
                      {u.must_change_password && (
                        <div className="text-[11px] text-amber-600">Trocar senha no 1º login</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {!isMe && u.role !== "admin" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setResetingId(resetingId === u.id ? null : u.id)}
                            data-testid={`reset-pwd-${u.id}`}
                            aria-label="Redefinir senha"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {!isMe && u.role !== "admin" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSendInvite(u)}
                            data-testid={`send-invite-${u.id}`}
                            aria-label="Reenviar convite"
                            title="Gerar nova senha e enviar por e-mail"
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(u)}
                          data-testid={`edit-user-${u.id}`}
                          aria-label="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {!isMe && u.role !== "admin" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(u)}
                            data-testid={`delete-user-${u.id}`}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            aria-label="Excluir"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      {resetingId === u.id && (
                        <div className="mt-2 flex justify-end gap-2">
                          <Input
                            type="password"
                            placeholder="Nova senha (≥ 6)"
                            value={resetPwd}
                            onChange={(e) => setResetPwd(e.target.value)}
                            className="w-44 h-8 text-xs"
                            data-testid={`reset-pwd-input-${u.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleResetPassword(u)}
                            data-testid={`reset-pwd-confirm-${u.id}`}
                            className="h-8 bg-brand-blue text-white hover:bg-brand-blue-dark"
                          >
                            <Save className="w-3 h-3 mr-1" /> Aplicar
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">
                    {search.trim()
                      ? `Nenhum usuário encontrado para "${search}".`
                      : "Nenhum usuário cadastrado."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="heading text-base font-semibold text-slate-900">
              {editing.id ? "Editar usuário" : "Novo usuário"}
            </h3>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                data-testid="user-name-input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input
                type="email"
                data-testid="user-email-input"
                value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                disabled={Boolean(editing.id)}
              />
            </div>
            <div className="space-y-2">
              <Label>Perfil</Label>
              <Select
                value={editing.profile_id}
                onValueChange={(v) => setEditing({ ...editing, profile_id: v })}
              >
                <SelectTrigger data-testid="user-profile-select">
                  <SelectValue placeholder="Selecione um perfil" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!editing.id && (
              <div className="space-y-2">
                <Label>Como definir a senha?</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, method: "password" })}
                    className={`flex-1 px-3 py-2 text-xs rounded-md border ${
                      editing.method === "password"
                        ? "bg-brand-blue text-white border-brand-blue"
                        : "bg-white text-slate-600 border-slate-300"
                    }`}
                  >
                    Definir agora
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, method: "invite" })}
                    className={`flex-1 px-3 py-2 text-xs rounded-md border ${
                      editing.method === "invite"
                        ? "bg-brand-blue text-white border-brand-blue"
                        : "bg-white text-slate-600 border-slate-300"
                    }`}
                  >
                    Enviar convite por e-mail
                  </button>
                </div>
              </div>
            )}

            {!editing.id && editing.method === "password" && (
              <div className="space-y-2 md:col-span-2">
                <Label>Senha provisória</Label>
                <Input
                  type="password"
                  data-testid="user-password-input"
                  value={editing.password}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
            )}

            {editing.id && (
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={editing.is_active}
                    onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                    data-testid="user-active-switch"
                  />
                  <span className="text-sm text-slate-700">
                    {editing.is_active ? "Ativo" : "Desativado"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              data-testid="save-user-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editing.id ? "Salvar" : editing.method === "invite" ? "Criar e enviar convite" : "Criar usuário"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
